import { useRoute } from '@react-navigation/native';
import { WebviewScreenLayout } from '../WebviewScreenLayout';
import { useNavigationGoBack, useWebviewUri } from '../../hooks';
import { isNotNil } from '../../utils';
import {
  fillWebRoutePath,
  WEB_ROUTE_PATHS,
  type ScreenName,
} from '../../webRoutePaths';
import { type ComponentProps, useState } from 'react';
import { StackScreen } from '@banhala/ui-kit';

/**
 * 화면에 들어오는 route 파라미터. path 세그먼트(`:orderSno` 등)와 그 밖의 쿼리가 섞여 온다
 * (react-navigation linking 이 둘을 구분하지 않고 route.params 로 넘긴다).
 */
type WebviewRouteParams = Record<string, string | undefined>;

type CreateWebviewScreenOption = {
  /** `WEB_ROUTE_PATHS` 의 키. 이 화면이 열 경로를 여기서 가져온다. */
  screen: ScreenName;
  /** 네이티브 헤더 타이틀. `showHeader` 가 false 면 화면 title 로만 쓰인다. */
  title: string;
  updateTitle?: boolean;
  /**
   * 네이티브 헤더를 그릴지. 기본 false — 대상 페이지들이 자체 타이틀바를 그린다
   * (`useWebviewUri` 는 `title_bar=false` 를 붙이지 않는다). 켜면 헤더가 두 개가 된다.
   */
  showHeader?: boolean;
  rightIcons?: ComponentProps<typeof StackScreen>['rightIcons'];
};

const PATH_PARAM_PATTERN = /:(\w+)/g;

/** 경로 패턴의 동적 세그먼트 이름들. (`/orders/:orderSno/cancellations` → `['orderSno']`) */
const getPathParamNames = (pattern: string): string[] =>
  [...pattern.matchAll(PATH_PARAM_PATTERN)].map(([, name]) => name);

/**
 * 이 쿼리 파라미터가 붙어 들어오면 네이티브 헤더를 숨긴다(`showHeader: true` 인 화면도).
 *
 * 콘텐츠가 자체 헤더를 그리는 모바일 진입을 뜻하므로, 우리 헤더까지 그리면 헤더가 두 개가 된다.
 * 값은 웹뷰 URL 쿼리로도 그대로 승계되므로 콘텐츠도 같은 플래그를 볼 수 있다.
 */
const HIDE_HEADER_QUERY_KEY = 'should_open_mobile';

/** `?should_open_mobile` / `=true` / `=1` 은 참. 명시적 `false`/`0` 만 거짓으로 본다. */
const isHideHeaderRequested = (value: string | undefined): boolean =>
  isNotNil(value) && value !== 'false' && value !== '0';

/**
 * "내용은 웹뷰지만 주소를 유지해야 해서 전용 화면으로 둔" 화면들을 만드는 팩토리.
 *
 * 전용 화면으로 두는 이유는 Cart / OrderDetail 과 같다 — 범용 `WebViewScreen` 으로 열면
 * 목적지가 `url` params 문자열로 들어가 주소가 그 경로로 유지되지 않는다(뒤에 인코딩된
 * URL 이 붙는 형태). Navigator 에 `path` 로 등록해 주소를 유지한다.
 *
 * 동적 세그먼트가 있는 경로(`/orders/:orderSno/cancellations`)도 처리한다 — 세그먼트는
 * 경로에 채우고 나머지 파라미터는 웹뷰 URL 쿼리로 승계한다. 세그먼트 값이 아직 없으면
 * 웹뷰를 렌더하지 않는다(`useWebviewUri` 가 undefined 를 그대로 통과시킨다).
 *
 * 화면마다 다른 동작(웹 타이틀 수신, 헤더 아이콘 등)이 필요하면 이 팩토리 대신 개별 파일로
 * 두는 게 맞다(EventDetail 처럼).
 */
export const createWebviewScreen = ({
  screen,
  title,
  rightIcons,
  updateTitle = false,
  showHeader = false,
}: CreateWebviewScreenOption) => {
  const pathParamNames = getPathParamNames(WEB_ROUTE_PATHS[screen]);

  const WebviewScreen = () => {
    const route = useRoute();
    const [screenTitle, setScreenTitle] = useState(title);
    const params = (route.params ?? {}) as WebviewRouteParams;
    const navigationGoBack = useNavigationGoBack();

    const hasAllPathParams = pathParamNames.every(name =>
      isNotNil(params[name]),
    );
    const query = Object.fromEntries(
      Object.entries(params).filter(([key]) => !pathParamNames.includes(key)),
    );

    const uri = useWebviewUri(
      hasAllPathParams ? fillWebRoutePath(screen, params) : undefined,
      query,
    );

    // `should_open_mobile` 로 들어오면 콘텐츠가 자체 헤더를 그리므로 네이티브 헤더를 숨긴다.
    const hideHeaderByQuery = isHideHeaderRequested(
      params[HIDE_HEADER_QUERY_KEY],
    );

    return (
      <WebviewScreenLayout
        title={screenTitle}
        uri={uri}
        showHeader={showHeader && !hideHeaderByQuery}
        onPressBack={navigationGoBack}
        rightIcons={rightIcons}
        onChangeTitle={updateTitle ? setScreenTitle : undefined}
      />
    );
  };

  // 개발 도구/에러 스택에서 화면을 구분할 수 있게 이름을 붙인다(팩토리라 익명 함수가 된다).
  WebviewScreen.displayName = `${screen}Screen`;

  return WebviewScreen;
};
